import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, AlertTriangle, BarChart3, ChevronLeft, ChevronRight, Clock, Copy, Database, Eye, Gauge, Info, Loader2, RefreshCw, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/lib/auth';
import { isTenantDeveloperUser } from '@/lib/developer-access';
import { cn } from '@/lib/utils';

type PagePerfTrace = {
  id: string;
  trace_id: string;
  page_load_id: string;
  tenant_id: string | null;
  user_id: string | null;
  route: string;
  started_at: string | null;
  ended_at: string | null;
  duration_ms: number;
  navigation_type: string | null;
  visibility_state: string | null;
  api_count: number;
  api_error_count: number;
  api_total_ms: number;
  api_max_ms: number;
  dom_node_count: number | null;
  long_task_count: number;
  payload?: {
    final?: boolean;
    flushReason?: string | null;
    apiTimings?: Array<{
      method: string;
      path: string;
      status: number;
      durationMs: number;
      responseBytes?: number;
      serverTiming?: string | null;
      error?: string | null;
      startedAtMs?: number;
    }>;
    traceWindowMs?: number;
    loadSettledReason?: string | null;
    userAgent?: string | null;
  };
  created_at: string;
  updated_at: string;
};

type PerfResponse = {
  items: PagePerfTrace[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
};

const PAGE_SIZE = 25;

function formatTime(value: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-SG', { hour12: false });
}

function formatMs(value: number | null | undefined) {
  const ms = Math.round(Number(value ?? 0));
  return `${ms.toLocaleString()}ms`;
}

function durationClass(ms: number) {
  if (ms >= 10_000) return 'text-red-600';
  if (ms >= 5_000) return 'text-orange-600';
  if (ms >= 1_500) return 'text-yellow-700';
  return 'text-green-700';
}

function p95(values: number[]) {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.ceil(sorted.length * 0.95) - 1;
  return sorted[Math.max(0, index)] ?? 0;
}

function shortId(value: string | null | undefined) {
  if (!value) return '-';
  return value.length > 18 ? `${value.slice(0, 12)}...` : value;
}

function copyText(value: string) {
  void navigator.clipboard?.writeText(value);
}

function apiStatusClass(status: number) {
  if (status >= 500 || status === 0) return 'destructive' as const;
  if (status >= 400) return 'destructive' as const;
  return 'secondary' as const;
}

function parseServerAppMs(serverTiming: string | null | undefined): number | null {
  if (!serverTiming) return null;
  const match = /app;dur=([0-9.]+)/.exec(serverTiming);
  return match ? Math.round(Number(match[1])) : null;
}

type PerfApiTiming = NonNullable<NonNullable<PagePerfTrace['payload']>['apiTimings']>[number];

function normalizeApiPath(path: string): string {
  return path.split('?')[0] ?? path;
}

function isRouteRelatedApi(route: string, apiPath: string): boolean {
  const cleanRoute = normalizeApiPath(route);
  const cleanApi = normalizeApiPath(apiPath);

  if (cleanRoute === '/admin/page-performance') return cleanApi === '/perf/page-traces';
  if (cleanRoute === '/oms/schedule') return cleanApi === '/daily-schedule' || cleanApi.startsWith('/oms/');
  if (cleanRoute.startsWith('/tms/fcl-planner')) return cleanApi.startsWith('/tms/') || cleanApi.startsWith('/jobs') || cleanApi.startsWith('/entities/jobs');
  if (cleanRoute.startsWith('/hms/planner')) return cleanApi.startsWith('/tms/fcl/') || cleanApi.startsWith('/tms/tr128/') || cleanApi.startsWith('/jobs') || cleanApi.startsWith('/entities/jobs');
  if (cleanRoute.startsWith('/hms/')) return cleanApi.startsWith('/hms/') || cleanApi.startsWith('/entities/jobs');
  if (cleanRoute.startsWith('/wms/')) return cleanApi.startsWith('/wms/') || cleanApi.startsWith('/warehouse/');
  if (cleanRoute.startsWith('/settings/')) return cleanApi.startsWith('/auth/tenant/') || cleanApi.startsWith('/settings/');
  if (cleanRoute.startsWith('/admin/')) return cleanApi.startsWith('/admin/') || cleanApi.startsWith('/platform/');

  const firstSegment = cleanRoute.split('/').filter(Boolean)[0];
  return firstSegment ? cleanApi.startsWith(`/${firstSegment}/`) : true;
}

function relatedApiTimings(row: PagePerfTrace): PerfApiTiming[] {
  const apiTimings = row.payload?.apiTimings ?? [];
  return apiTimings.filter((timing) => isRouteRelatedApi(row.route, timing.path));
}

function summarizeTrace(row: PagePerfTrace, apiTimings = relatedApiTimings(row)) {
  const capturedApiTimings = row.payload?.apiTimings ?? [];
  const sortedApi = [...apiTimings].sort((a, b) => Number(b.durationMs ?? 0) - Number(a.durationMs ?? 0));
  const slowest = sortedApi[0];
  const apiTotal = apiTimings.reduce((sum, item) => sum + Number(item.durationMs ?? 0), 0);
  const pageMs = Number(row.duration_ms ?? 0);
  const apiShare = pageMs > 0 ? Math.round((apiTotal / pageMs) * 100) : 0;
  const capturedOnlyCount = capturedApiTimings.length - apiTimings.length;

  if (apiTimings.length === 0 && capturedOnlyCount > 0) {
    return {
      tone: 'secondary' as const,
      title: 'Background calls filtered',
      detail: `${capturedOnlyCount} captured API call(s) do not match this route and are hidden from the related list.`,
    };
  }
  if (apiTimings.some((item) => item.status >= 400 || item.status === 0)) {
    return {
      tone: 'destructive' as const,
      title: 'API errors detected',
      detail: 'One or more related API call(s) failed. Start with the failed calls in the detail list.',
    };
  }
  if (Number(slowest?.durationMs ?? 0) >= 1000) {
    return {
      tone: 'destructive' as const,
      title: 'Single slow API',
      detail: `${slowest?.method ?? 'API'} ${slowest?.path ?? ''} is the slowest call at ${formatMs(slowest?.durationMs)}.`,
    };
  }
  if (
    pageMs >= 10_000
    && Number(slowest?.durationMs ?? 0) < 1000
    && row.long_task_count === 0
    && (!row.payload?.traceWindowMs || row.payload?.flushReason === 'route-change' || row.payload?.flushReason === 'pagehide')
  ) {
    return {
      tone: 'secondary' as const,
      title: 'Trace window + fan-out',
      detail: `The page number is likely inflated by the trace window. The slowest related API is only ${formatMs(slowest?.durationMs)}, but related API work created ${formatMs(apiTotal)} total measured work.`,
    };
  }
  if (apiTimings.length >= 30) {
    return {
      tone: 'secondary' as const,
      title: 'API fan-out',
      detail: `${apiTimings.length} related API calls were made. No single call is extreme, but the total related API work is ${formatMs(apiTotal)}.`,
    };
  }
  if (pageMs >= 5000 && apiShare < 40 && row.long_task_count === 0) {
    return {
      tone: 'secondary' as const,
      title: 'Trace window / idle wait',
      detail: 'The page duration is much larger than measured API work and no long browser tasks were captured.',
    };
  }
  if (row.long_task_count > 0) {
    return {
      tone: 'secondary' as const,
      title: 'Browser main thread work',
      detail: `${row.long_task_count} long browser task(s) were observed during this trace.`,
    };
  }
  return {
    tone: 'secondary' as const,
    title: 'No single bottleneck',
    detail: 'No failed API, single slow API, or browser long task stands out in this trace.',
  };
}

function relativeApiStart(apiTimings: NonNullable<PagePerfTrace['payload']>['apiTimings'], startedAtMs?: number) {
  if (!apiTimings?.length || startedAtMs === undefined) return null;
  const startedValues = apiTimings
    .map((item) => item.startedAtMs)
    .filter((value): value is number => typeof value === 'number');
  if (startedValues.length === 0) return null;
  return Math.max(0, Math.round(startedAtMs - Math.min(...startedValues)));
}

export default function PagePerformancePage() {
  const { user } = useAuth();
  const canViewTechnicalTools = isTenantDeveloperUser(user);
  const [rows, setRows] = useState<PagePerfTrace[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [route, setRoute] = useState('');
  const [traceId, setTraceId] = useState('');
  const [minDurationMs, setMinDurationMs] = useState('1500');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTrace, setSelectedTrace] = useState<PagePerfTrace | null>(null);

  const fetchRows = useCallback(async () => {
    if (!canViewTechnicalTools) {
      setRows([]);
      setTotal(0);
      setError('Developer access is required for page performance traces.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<PerfResponse>('/perf/page-traces', {
        page,
        limit: PAGE_SIZE,
        route: route.trim() || undefined,
        traceId: traceId.trim() || undefined,
        minDurationMs: minDurationMs.trim() || undefined,
      });
      setRows(data.items ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      setRows([]);
      setTotal(0);
      setError(err instanceof Error ? err.message : 'Failed to load page performance traces');
    } finally {
      setLoading(false);
    }
  }, [canViewTechnicalTools, minDurationMs, page, route, traceId]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const stats = useMemo(() => {
    const durations = rows.map((row) => Number(row.duration_ms ?? 0));
    const apiDurations = rows.map((row) => Number(row.api_total_ms ?? 0));
    const slowCount = rows.filter((row) => Number(row.duration_ms ?? 0) >= 5000).length;
    const apiErrors = rows.reduce((sum, row) => sum + Number(row.api_error_count ?? 0), 0);
    return {
      avgPageMs: durations.length ? Math.round(durations.reduce((sum, item) => sum + item, 0) / durations.length) : 0,
      p95PageMs: Math.round(p95(durations)),
      avgApiMs: apiDurations.length ? Math.round(apiDurations.reduce((sum, item) => sum + item, 0) / apiDurations.length) : 0,
      slowCount,
      apiErrors,
    };
  }, [rows]);

  const selectedSummary = selectedTrace ? summarizeTrace(selectedTrace) : null;
  const selectedAllApiTimings = selectedTrace?.payload?.apiTimings ?? [];
  const selectedApiTimings = useMemo(() => {
    const timings = selectedTrace ? relatedApiTimings(selectedTrace) : [];
    return [...timings].sort((a, b) => Number(b.durationMs ?? 0) - Number(a.durationMs ?? 0));
  }, [selectedTrace]);
  const selectedFilteredApiCount = Math.max(0, selectedAllApiTimings.length - selectedApiTimings.length);
  const selectedRelatedApiTotalMs = selectedApiTimings.reduce((sum, item) => sum + Number(item.durationMs ?? 0), 0);
  const selectedRelatedApiMaxMs = Math.max(0, ...selectedApiTimings.map((item) => Number(item.durationMs ?? 0)));

  const resetToSlowPages = () => {
    setPage(1);
    setMinDurationMs('5000');
  };

  const clearFilters = () => {
    setPage(1);
    setRoute('');
    setTraceId('');
    setMinDurationMs('');
  };

  if (!canViewTechnicalTools) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-10">
            <h1 className="text-xl font-semibold">Developer access required</h1>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Technical diagnostics are only available to platform admins and the tenant developer account.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Activity className="h-6 w-6 text-primary" />
            Page Performance
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Trace page load time, API timing, DOM weight, and browser-to-API correlation.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={resetToSlowPages}>
            <Clock className="mr-1 h-4 w-4" />
            Slow pages
          </Button>
          <Button variant="outline" size="sm" onClick={() => void fetchRows()}>
            <RefreshCw className={cn('mr-1 h-4 w-4', loading && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Rows</p>
            <p className="text-2xl font-bold">{total.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Avg Page</p>
            <p className={cn('text-2xl font-bold', durationClass(stats.avgPageMs))}>{formatMs(stats.avgPageMs)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">P95 Page</p>
            <p className={cn('text-2xl font-bold', durationClass(stats.p95PageMs))}>{formatMs(stats.p95PageMs)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Avg API Total</p>
            <p className="text-2xl font-bold text-primary">{formatMs(stats.avgApiMs)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Slow / Errors</p>
            <p className="text-2xl font-bold">{stats.slowCount} / {stats.apiErrors}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Search className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 md:grid-cols-[1.5fr_1.5fr_1fr_auto_auto]">
            <Input
              value={route}
              onChange={(event) => { setPage(1); setRoute(event.target.value); }}
              placeholder="/tms/fcl-planner or /oms/schedule"
            />
            <Input
              value={traceId}
              onChange={(event) => { setPage(1); setTraceId(event.target.value); }}
              placeholder="trace id"
              className="font-mono text-xs"
            />
            <Input
              value={minDurationMs}
              onChange={(event) => { setPage(1); setMinDurationMs(event.target.value); }}
              placeholder="min ms"
              inputMode="numeric"
            />
            <Button variant="secondary" onClick={() => void fetchRows()} disabled={loading}>Apply</Button>
            <Button variant="ghost" onClick={clearFilters}>Clear</Button>
          </div>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <BarChart3 className="h-4 w-4" />
            Recent Traces
          </CardTitle>
          <span className="text-xs text-muted-foreground">Page {page}</span>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Route</TableHead>
                <TableHead>Page</TableHead>
                <TableHead>API</TableHead>
                <TableHead>DOM</TableHead>
                <TableHead>Trace</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Discovery</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} className="h-32 text-center text-muted-foreground">
                    <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                    Loading traces
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="h-32 text-center text-muted-foreground">
                    No page performance traces found
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.map((row) => {
                const summary = summarizeTrace(row);
                return (
                <TableRow key={row.id} className="cursor-pointer" onClick={() => setSelectedTrace(row)}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatTime(row.created_at)}</TableCell>
                  <TableCell className="max-w-[320px] truncate font-mono text-xs" title={row.route}>{row.route}</TableCell>
                  <TableCell className={cn('font-mono text-xs font-semibold', durationClass(row.duration_ms))}>
                    {formatMs(row.duration_ms)}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="font-mono">{formatMs(row.api_total_ms)}</div>
                    <div className="text-muted-foreground">{row.api_count} calls · max {formatMs(row.api_max_ms)}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{(row.dom_node_count ?? 0).toLocaleString()} nodes</div>
                    <div className="text-muted-foreground">{row.long_task_count} long tasks</div>
                  </TableCell>
                  <TableCell className="max-w-[170px] text-xs">
                    <button
                      type="button"
                      onClick={(event) => { event.stopPropagation(); copyText(row.trace_id); }}
                      className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[11px] hover:bg-muted"
                      title={row.trace_id}
                    >
                      {shortId(row.trace_id)}
                      <Copy className="h-3 w-3" />
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col items-start gap-1">
                      <Badge variant={row.api_error_count > 0 ? 'destructive' : 'secondary'} className="text-[10px]">
                        {row.api_error_count > 0 ? `${row.api_error_count} API errors` : 'OK'}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">{row.payload?.flushReason ?? '-'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge variant={summary.tone} className="max-w-[140px] truncate text-[10px]" title={summary.detail}>
                        {summary.title}
                      </Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={(event) => { event.stopPropagation(); setSelectedTrace(row); }}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{rows.length} shown · {total.toLocaleString()} total</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" disabled={rows.length < PAGE_SIZE || loading} onClick={() => setPage((value) => value + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Sheet open={Boolean(selectedTrace)} onOpenChange={(open) => { if (!open) setSelectedTrace(null); }}>
        <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-[980px]">
          {selectedTrace && (
            <>
              <SheetHeader className="border-b px-6 py-5">
                <SheetTitle className="flex items-center gap-2">
                  <Gauge className="h-5 w-5 text-primary" />
                  Performance Discovery
                </SheetTitle>
                <SheetDescription className="font-mono text-xs">
                  {selectedTrace.route}
                </SheetDescription>
              </SheetHeader>

              <ScrollArea className="min-h-0 flex-1">
                <div className="space-y-4 p-6">
                  <div className="grid gap-3 md:grid-cols-4">
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-xs text-muted-foreground">Page Load</p>
                        <p className={cn('text-xl font-bold', durationClass(selectedTrace.duration_ms))}>{formatMs(selectedTrace.duration_ms)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-xs text-muted-foreground">Related API Work</p>
                        <p className="text-xl font-bold">{formatMs(selectedRelatedApiTotalMs)}</p>
                        <p className="text-xs text-muted-foreground">{selectedApiTimings.length} calls · max {formatMs(selectedRelatedApiMaxMs)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-xs text-muted-foreground">Browser</p>
                        <p className="text-xl font-bold">{(selectedTrace.dom_node_count ?? 0).toLocaleString()} nodes</p>
                        <p className="text-xs text-muted-foreground">{selectedTrace.long_task_count} long tasks</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-xs text-muted-foreground">Trace Window</p>
                        <p className="text-xl font-bold">{formatMs(selectedTrace.payload?.traceWindowMs ?? selectedTrace.duration_ms)}</p>
                        <p className="text-xs text-muted-foreground">{selectedTrace.payload?.flushReason ?? '-'}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {selectedSummary && (
                    <Card>
                      <CardContent className="flex gap-3 pt-4">
                        {selectedSummary.tone === 'destructive'
                          ? <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
                          : <Info className="mt-0.5 h-5 w-5 text-primary" />}
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold">{selectedSummary.title}</p>
                            <Badge variant={selectedSummary.tone}>{selectedTrace.api_error_count > 0 ? 'action required' : 'diagnosis'}</Badge>
                          </div>
                          <p className="mt-1 text-sm text-muted-foreground">{selectedSummary.detail}</p>
                          <p className="mt-2 text-xs text-muted-foreground">
                            The table below shows route-related API calls only. Background calls from other mounted tabs are filtered out to avoid false diagnosis.
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-sm">
                          <Activity className="h-4 w-4" />
                          Trace Facts
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm">
                        <div className="grid grid-cols-[120px_1fr] gap-2">
                          <span className="text-muted-foreground">Trace ID</span>
                          <button type="button" className="truncate text-left font-mono text-xs underline-offset-2 hover:underline" onClick={() => copyText(selectedTrace.trace_id)}>
                            {selectedTrace.trace_id}
                          </button>
                          <span className="text-muted-foreground">Page Load ID</span>
                          <span className="truncate font-mono text-xs">{selectedTrace.page_load_id}</span>
                          <span className="text-muted-foreground">Started</span>
                          <span>{formatTime(selectedTrace.started_at)}</span>
                          <span className="text-muted-foreground">Ended</span>
                          <span>{formatTime(selectedTrace.ended_at)}</span>
                          <span className="text-muted-foreground">Load settled</span>
                          <span>{selectedTrace.payload?.loadSettledReason ?? selectedTrace.payload?.flushReason ?? '-'}</span>
                          <span className="text-muted-foreground">Visibility</span>
                          <span>{selectedTrace.visibility_state ?? '-'}</span>
                        </div>
                        <Separator />
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-muted-foreground">How to read this trace</p>
                          <ul className="space-y-1 text-xs text-muted-foreground">
                            <li>Page Load: benchmark duration until the page becomes API-idle.</li>
                            <li>Trace Window: total time before the trace was flushed or route changed.</li>
                            <li>Max API: best signal for one slow backend endpoint.</li>
                            <li>API Count: signal for frontend fan-out and repeated calls.</li>
                          </ul>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-sm">
                          <Database className="h-4 w-4" />
                          Related API Timing Detail
                        </CardTitle>
                        {selectedFilteredApiCount > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {selectedFilteredApiCount} captured background call(s) hidden from this route-focused list.
                          </p>
                        )}
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="max-h-[520px] overflow-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>API</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Client</TableHead>
                                <TableHead>Server</TableHead>
                                <TableHead>Start</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {selectedApiTimings.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={5} className="h-24 text-center text-sm text-muted-foreground">
                                    No route-related API timings captured for this trace.
                                  </TableCell>
                                </TableRow>
                              )}
                              {selectedApiTimings.map((apiTiming, index) => {
                                const serverMs = parseServerAppMs(apiTiming.serverTiming);
                                const startOffset = relativeApiStart(selectedTrace.payload?.apiTimings, apiTiming.startedAtMs);
                                return (
                                  <TableRow key={`${apiTiming.method}-${apiTiming.path}-${index}`}>
                                    <TableCell className="max-w-[360px]">
                                      <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="font-mono text-[10px]">{apiTiming.method}</Badge>
                                        <span className="truncate font-mono text-xs" title={apiTiming.path}>{apiTiming.path}</span>
                                      </div>
                                      {apiTiming.error && <p className="mt-1 text-xs text-red-600">{apiTiming.error}</p>}
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant={apiStatusClass(apiTiming.status)} className="font-mono text-[10px]">
                                        {apiTiming.status}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className={cn('font-mono text-xs font-semibold', durationClass(apiTiming.durationMs))}>
                                      {formatMs(apiTiming.durationMs)}
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">
                                      {serverMs === null ? '-' : formatMs(serverMs)}
                                    </TableCell>
                                    <TableCell className="font-mono text-xs text-muted-foreground">
                                      {startOffset === null ? '-' : `+${formatMs(startOffset)}`}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </ScrollArea>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
